import { Button } from 'primereact/button';
import { Calendar, LocaleSettings } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { getValueOfTag, modifyValueOfTag, separatorKey } from '../../../../../../form/components/FormCheckbox';
import { QuestionType } from '../../../../../../form/enum/QuestionType';
import { FormQuestion } from '../../../../../../form/models/FormQuestion';

interface Props {
  visible: boolean;
  subQuestion: any;
  currentQuestion: FormQuestion;
  onHide: () => void;
  onSave: (updatedQuestion: FormQuestion) => void;
  editValue: (props: any, comeFromSubQuestion?: boolean) => JSX.Element;
  editImageValue: (props: any, comeFromSubQuestion?: boolean) => JSX.Element;
  editUnit?: (props: any) => JSX.Element;
  editValidation?: (props: any, field: 'min' | 'max' | 'step') => JSX.Element;
  actionBodyTemplate: (row: any) => React.ReactNode;
  onSubQuestionChange?: (updatedSubQuestion: any) => void;
  editKeyValue?: (props : any, key: any, fromSubQuestion?: boolean) => JSX.Element;
  prismicKeys: string[];
  getLocale: () => LocaleSettings | undefined;
  decisionTreeList: any[];
}

//region PRINCIPAL

export const EditSubQuestionDialog = ((props: Props) => {
  const [editingRow, setEditingRow] = React.useState<number | null>(null);

  const {
    visible, subQuestion,
    currentQuestion,
    onHide, onSave
  } = props;

  if (!subQuestion) return null;

  const type = subQuestion.type;
  const footer = getFooter( onHide, () => onSave(currentQuestion));

  let dialogBody: React.ReactNode;
  switch (type) {
    case QuestionType.NUMBER: dialogBody = renderNumberTable(subQuestion, props); break;

    case QuestionType.RADIO_IMAGE:
    case QuestionType.MATRIX: dialogBody = renderImageValueTable(subQuestion, props); break;

    case QuestionType.CHECKBOX: dialogBody = <CheckboxEditor {...props} subQuestion={subQuestion} />; break;

    case QuestionType.DATE_PICKER: dialogBody = <DatePickerEditor {...props} subQuestion={subQuestion} />; break;

    case QuestionType.DROPDOWN: dialogBody = <DropdownSubEditor {...props} subQuestion={subQuestion}  />; break;


    default: dialogBody = <div>No editor implemented for type: {type}</div>;
  }

  return (
    <Dialog header="Update deep options"
            visible={visible}
            modal
            style={{ width: '1000px' }}
            onHide={onHide}
            footer={footer} >

      {dialogBody}

    </Dialog>
  )
})

function getFooter(onClose: () => void, onSave: () => void) {
  return (
    <div>
      <Button
        label="Close"
        className="p-button-danger"
        style={{ backgroundColor: '#0087b7', borderColor: '#0087b7' }}
        icon="pi pi-times"
        onClick={onClose}
      />
      <Button
        label="Save"
        className="p-button-danger"
        style={{ backgroundColor: '#e98b3a', borderColor: '#e98b3a' }}
        icon="pi pi-check"
        onClick={onSave}
      />
    </div>
  );
}

//endregion

//region MATRIX && RADIO-IMAGES

function renderImageValueTable(subQuestion: any, props: Props) {
  const tableData =
    subQuestion?.values?.map((val: string, i: number) => ({
      value: val,
      img: subQuestion?.imageValues?.[i] ?? ''
    })) ?? [];

  return (
    <div className="valuesTable">
      <DataTable value={tableData} editMode="row" reorderableColumns>
        <Column field="value" header="Values" editor={(p) => props.editValue(p, true)} />
        <Column field="img" header="Image values" editor={(p) => props.editImageValue(p, true)} />
        <Column rowEditor headerStyle={{ width: '7rem' }} bodyStyle={{ textAlign: 'center' }} />
        <Column body={(p, r) => props.actionBodyTemplate(r)} />
        <Column rowReorder rowReorderIcon="pi pi-ellipsis-v" style={{ width: '3em', cursor: 'move' }} />
      </DataTable>
    </div>
  );
}

//endregion

//region NUMBER

function renderNumberTable(subQuestion: any, props: Props) {
  const updateValidation = (field: 'min' | 'max' | 'step', value: number | null) => {
    const updatedValidation = {
      ...subQuestion.validation,
      [field]: value
    };
    props.onSubQuestionChange?.({
      ...subQuestion,
      validation: updatedValidation
    });
  };

  return (
    <div className="valuesTable">
      <div className="param">
        <label>Min:</label>
        <InputText
          type="number"
          className="p-inputtext-sm block mb-2"
          placeholder="Enter value"
          value={subQuestion?.validation?.min ?? ''}
          onChange={(e: any) =>
            updateValidation('min', parseFloat(e.target.value))
          }
        />
      </div>

      <div className="param">
        <label>Max:</label>
        <InputText
          type="number"
          className="p-inputtext-sm block mb-2"
          placeholder="Enter value"
          value={subQuestion?.validation?.max ?? ''}
          onChange={(e: any) =>
            updateValidation('max', parseFloat(e.target.value))
          }
        />
      </div>

      <div className="param">
        <label>Step:</label>
        <InputText
          type="number"
          className="p-inputtext-sm block mb-2"
          placeholder="Enter value"
          value={subQuestion?.validation?.step ?? ''}
          onChange={(e: any) =>
            updateValidation('step', parseFloat(e.target.value))
          }
        />
      </div>

      <div className="param">
        <label id="unitTooltip">Unit:</label>
        <Dropdown
          value={{ name: subQuestion.unit, label: subQuestion.unit }}
          options={ props.prismicKeys }
          onChange={(e) => {
            const selected = e.value;
            props.onSubQuestionChange?.({
              ...subQuestion,
              unit: selected.name
            });
          }}
          placeholder="Select a value"
          filter
          appendTo={document.body}
        />
        <Tooltip target="#unitTooltip" position="bottom">
          <p>Indication of the unit positioned to the right of the response box</p>
        </Tooltip>
      </div>
    </div>
  );
}

//endregion

//region CHECKBOX

const CheckboxEditor = (props: Props & { subQuestion: any }) => {
  const {
    subQuestion, onSubQuestionChange,
    editValue, editKeyValue,
    actionBodyTemplate } = props;

  const [editingRow, setEditingRow] = React.useState<number | null>(null);

  const tableData = subQuestion?.values?.map((val: string, i: number) => ({
    value: val,
    tooltip: getValueOfTag('[[TOOLTIP]]', val),
    info: getValueOfTag('[[INFO]]', val),
    separator: val.includes(separatorKey),
  })) ?? [];

  const updateCheckboxField = (
    index: number,
    field: 'tooltip' | 'info' | 'separator',
    value: any
  ) => {
    const updatedSub = { ...subQuestion };

    switch (field) {
      case 'tooltip':
        updatedSub.tooltips = [...(subQuestion.tooltips ?? [])];
        updatedSub.tooltips[index] = value;
        break;
      case 'info':
        updatedSub.infos = [...(subQuestion.infos ?? [])];
        updatedSub.infos[index] = value;
        break;
      case 'separator':
        const currentVal = updatedSub.values?.[index] ?? '';

        if (value) {
          if (!currentVal.includes(separatorKey)) {
            updatedSub.values[index] = separatorKey + currentVal;
          }
        }
        else {
          updatedSub.values[index] = currentVal.replace(separatorKey, '');
        }

        updatedSub.separators = [...(subQuestion.separators ?? [])];
        updatedSub.separators[index] = value;
        break;
    }

    onSubQuestionChange?.(updatedSub);
  };

  return (
    <div className="valuesTable">
      <DataTable
        value={tableData}
        editMode="row"
        reorderableColumns
        onRowEditInit={(e) => setEditingRow(e.index)}
        onRowEditCancel={() => setEditingRow(null)}
        onRowEditSave={() => setEditingRow(null)}
      >
        <Column
          field="value"
          header="Values"
          editor={(p) => editValue(p, true)}
          body={(rowData) => (
            <div style={{
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              maxWidth: '600px',
            }}>
              {rowData.value}
            </div>
          )}
        />
        <Column
          field="tooltip"
          header="Tooltip"
          editor={(p) => editKeyValue?.(p, '[[TOOLTIP]]', true)}
          body={(rowData) => <span>{rowData.tooltip}</span>}
        />
        <Column
          field="info"
          header="Info"
          editor={(p) => editKeyValue?.(p, '[[INFO]]', true)}
          body={(rowData) => <span>{rowData.info}</span>}
        />
        <Column
          field="separator"
          header="Separator"
          body={(rowData, row) => (
            <Checkbox
              checked={rowData.separator}
              disabled={row.rowIndex !== editingRow}
              onChange={(e) => {
                updateCheckboxField(row.rowIndex, 'separator', e.checked);
              }}
            />
          )}
          style={{ textAlign: 'center', width: '8rem' }}
        />
        <Column rowEditor headerStyle={{ width: '7rem' }} bodyStyle={{ textAlign: 'center' }} />
        <Column body={(p, r) => actionBodyTemplate(r)} />
        <Column rowReorder rowReorderIcon="pi pi-ellipsis-v" style={{ width: '3em', cursor: 'move' }} />
      </DataTable>

      {/* Tooltips */}
      <Tooltip target=".tooltip-tooltip" position="top" baseZIndex={10}>
        <p>A small magnifying glass will appear next to the proposal, and when the user moves the mouse over it, an explanatory text will appear.</p>
      </Tooltip>
      <Tooltip target=".info-tooltip" position="top" baseZIndex={10}>
        <p>A small thumbs-up will appear next to the proposal, and when the user moves the mouse over it, an informative text will appear.</p>
      </Tooltip>
    </div>
  );
};

//endregion

//region DATE PICKER

const DatePickerEditor = (props: Props & { subQuestion: any }) => {
  const { subQuestion, onSubQuestionChange } = props;

  const updateDate = (field: 'min' | 'max', date: Date | null) => {
    const formattedDate = date
      ? date.toLocaleDateString('sv', { year: 'numeric', month: '2-digit', day: '2-digit' }).replaceAll('/', '-')
      : null;

    onSubQuestionChange?.({
      ...subQuestion,
      size: {
        ...subQuestion.size,
        [field]: formattedDate
      }
    });
  };

  const getDate = (raw: string | null | undefined): Date | undefined => {
    if (!raw) return undefined;
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  };

  return (
    <div className="valuesTable">
      <div className="param">
        <label>Min: </label>
        <Calendar
          value={getDate(subQuestion?.size?.min)}
          onChange={(e) => updateDate('min', e.value instanceof Date ? e.value : e.value?.[0] ?? null)}
          dateFormat="dd/mm/yy"
          locale={props.getLocale()}
          monthNavigator
          yearNavigator
          yearRange="1900:2100"
          appendTo={document.body}
        />
      </div>

      <div className="param">
        <label>Max: </label>
        <Calendar
          value={getDate(subQuestion?.size?.max)}
          onChange={(e) => updateDate('max', e.value instanceof Date ? e.value : e.value?.[0] ?? null)}
          dateFormat="dd/mm/yy"
          locale={props.getLocale()}
          monthNavigator
          yearNavigator
          yearRange="1900:2100"
          appendTo={document.body}
        />
      </div>
    </div>
  );
};

//endregion

//region DROPDOWN

const DropdownSubEditor = (props: Props & { subQuestion: any }) => {
  const { subQuestion, onSubQuestionChange, editValue, actionBodyTemplate, prismicKeys } = props;

  const [dropdownType, setDropdownType] = React.useState(
    subQuestion?.target ? QuestionType.DECISION_TREE : "default"
  );

  const updateSubQuestion = (updated: any) => {
    onSubQuestionChange?.(updated);
  };

  const dropdownTypeOptions = [
    { name: "default", label: "default" },
    { name: QuestionType.DECISION_TREE, label: QuestionType.DECISION_TREE }
  ]

  const tableData = subQuestion?.values?.map((val: string) => ({ value: val })) ?? [];

  return (
    <div className="dropdownSubEditor">
      <Fieldset legend="Type">
        <label>Type: </label>
        <Dropdown
          value={dropdownTypeOptions.find(o => o.name === subQuestion.dropdownType) ?? "default"}
          optionLabel="label"
          options={dropdownTypeOptions}
          onChange={(e) => {
            const type = e.value.name;
            const updatedSub = { ...subQuestion };

            updatedSub.dropdownType = type;

            if (type === "default") {
              updatedSub.target = "";
            }

            setDropdownType(type);
            updateSubQuestion(updatedSub);
          }}
          placeholder="Select a type"
          filter
          editable
          appendTo={document.body}
        />

        {dropdownType === QuestionType.DECISION_TREE && (
          <>
            <br/>
            <br/>
            <label>Decision tree: </label>
            <Dropdown
              value={subQuestion.target}
              options={props.decisionTreeList}
              onChange={(e) => {
                const updatedSub = {
                  ...subQuestion,
                  target: e.value.name
                };
                updateSubQuestion(updatedSub);
              }}
              placeholder="Select a decision tree"
              filter
              editable
              appendTo={document.body}
            />
          </>
        )}
      </Fieldset>

      {dropdownType === "default" && (
        <div className="valuesTable">
          <DataTable value={tableData} editMode="row" reorderableColumns>
            <Column field="value" header="Values" editor={(p) => editValue(p, true)} />
            <Column rowEditor headerStyle={{ width: '7rem' }} bodyStyle={{ textAlign: 'center' }} />
            <Column body={(p, r) => actionBodyTemplate(r)} />
            <Column rowReorder rowReorderIcon="pi pi-ellipsis-v" style={{ width: '3em', cursor: 'move' }} />
          </DataTable>
        </div>
      )}
    </div>
  );
};

//endregion

